import { useMemo } from "react";
import { ExtendedAccount } from "@common/types/reports/types";
import {
    BreadcrumbItem,
    Breadcrumbs,
} from "@design-system/Breadcrumbs/Breadcrumbs";
import { CoaMappingSourceChartPane } from "../CoaMappingSourceChartPane";
import { CoaMappingRow } from "../coaMappingRowTypes";
import { CoaMappingViewConfig } from "../coaMappingViewConfig";
import { filterCoaMappingRowsByAccountIds } from "../filterCoaMappingRows";
import { CoaMappingPreview } from "./buildCoaMappingPreview";
import styles from "./CoaMappingPreviewStep.module.scss";

interface Props {
    account: ExtendedAccount;
    preview: CoaMappingPreview;
    sourceRows: CoaMappingRow[] | null;
    sourceViewConfig: CoaMappingViewConfig;
    showAccountNumbers: boolean;
}

function accountLabel(
    account: ExtendedAccount,
    showAccountNumbers: boolean,
): string {
    return showAccountNumbers && account.codeText !== null
        ? `${account.codeText} - ${account.name}`
        : account.name;
}

export const CoaMappingPreviewSourcesSidebar = ({
    account,
    preview,
    sourceRows,
    sourceViewConfig,
    showAccountNumbers,
}: Props) => {
    const mappedRows = useMemo(() => {
        if (!sourceRows) {
            return null;
        }
        const sources = preview.sourcesByAccountId.get(account.id) ?? [];
        return filterCoaMappingRowsByAccountIds({
            rows: sourceRows,
            accountIds: new Set(sources.map((source) => source.id)),
        });
    }, [sourceRows, preview, account.id]);

    return (
        <div className={styles.sidebar}>
            <header className={styles.sidebarHeader}>
                <Breadcrumbs>
                    <BreadcrumbItem>Mapped into</BreadcrumbItem>
                    <BreadcrumbItem current>
                        {accountLabel(account, showAccountNumbers)}
                    </BreadcrumbItem>
                </Breadcrumbs>
            </header>
            <div className={styles.sidebarBody}>
                <CoaMappingSourceChartPane
                    rows={mappedRows}
                    viewConfig={sourceViewConfig}
                />
            </div>
        </div>
    );
};
